/**
 * UTM Parameter Tracking & Attribution Utility for React SaaS App
 * Parses incoming marketing parameters from URL query, stores them, and formats them for Analytics.
 */

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
  'ref',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_PREFIX = 'tqa_utm_';
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/**
 * Gets top-level cookie domain if applicable (e.g. .thequickassist.co)
 */
function getCookieDomain(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  if (hostname.endsWith('.thequickassist.co') || hostname === 'thequickassist.co') {
    return '; domain=.thequickassist.co';
  }
  return '';
}

/**
 * Reads cookie by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
  } catch (err) {
    console.error('Error reading cookie:', err);
  }
  return null;
}

/**
 * Sets a cookie safely
 */
function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  try {
    const domain = getCookieDomain();
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${domain}`;
  } catch (err) {
    console.error('Error setting cookie:', err);
  }
}

/**
 * Captures incoming UTM parameters from URL search string and persists them.
 */
export function captureAndStoreUtmParams(searchParamsInput?: URLSearchParams | string): UtmParams {
  if (typeof window === 'undefined') return {};

  let searchParams: URLSearchParams;
  if (searchParamsInput instanceof URLSearchParams) {
    searchParams = searchParamsInput;
  } else if (typeof searchParamsInput === 'string') {
    searchParams = new URLSearchParams(searchParamsInput);
  } else {
    searchParams = new URLSearchParams(window.location.search);
  }

  const captured: UtmParams = {};

  UTM_KEYS.forEach((key) => {
    const value = searchParams.get(key);
    if (value && value.trim()) {
      const cleanVal = value.trim();
      captured[key] = cleanVal;

      try {
        sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, cleanVal);
        localStorage.setItem(`${STORAGE_PREFIX}${key}`, cleanVal);
        setCookie(`${STORAGE_PREFIX}${key}`, cleanVal);
      } catch (e) {
        console.error('Error storing UTM parameter:', e);
      }
    }
  });

  return getStoredUtmParams();
}

/**
 * Retrieves all stored UTM parameters from sessionStorage / localStorage / cookies.
 */
export function getStoredUtmParams(): UtmParams {
  if (typeof window === 'undefined') return {};

  const params: UtmParams = {};

  UTM_KEYS.forEach((key) => {
    let val: string | null = null;
    try {
      val =
        sessionStorage.getItem(`${STORAGE_PREFIX}${key}`) ||
        localStorage.getItem(`${STORAGE_PREFIX}${key}`) ||
        getCookie(`${STORAGE_PREFIX}${key}`);
    } catch {
      val = getCookie(`${STORAGE_PREFIX}${key}`);
    }

    if (val && val.trim()) {
      params[key] = val.trim();
    }
  });

  return params;
}

/**
 * Prepares formatted parameters payload for Google Analytics & Meta Pixel events.
 */
export function getGaCampaignParams(): Record<string, string> {
  const stored = getStoredUtmParams();
  const payload: Record<string, string> = {};

  if (stored.utm_source) payload.campaign_source = stored.utm_source;
  if (stored.utm_medium) payload.campaign_medium = stored.utm_medium;
  if (stored.utm_campaign) payload.campaign_name = stored.utm_campaign;
  if (stored.utm_term) payload.campaign_term = stored.utm_term;
  if (stored.utm_content) payload.campaign_content = stored.utm_content;
  if (stored.gclid) payload.gclid = stored.gclid;
  if (stored.fbclid) payload.fbclid = stored.fbclid;

  return payload;
}
